import { useEffect, useRef, useState } from 'react'
import { InView } from 'react-intersection-observer'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import formatTotalizersTotal from 'src/utils/amplitude/formatTotalizersTotal'
import { sendNudgetInstallmentBarEvent } from 'src/utils/amplitude/nudgetInstallmentBarEvent'

import {
  formatCurrency,
  getCashDiscountBenefitIds,
  getCashDiscountValue,
  getHighlightedStep,
  getProgressPercent,
  getProgressWidthClass,
  getTotalDiscountValue,
  INSTALLMENT_TIERS,
  MAX_INSTALLMENT_TIER,
  parseCurrencyLabelToNumber,
  roundToTwoDecimalPlaces,
  VISUAL_STEPS,
} from './utils'

type CartFeedbackInstallmentNudgeProps = {
  totalCartLabel?: string
}

const CartFeedbackInstallmentNudge = ({
  totalCartLabel,
}: CartFeedbackInstallmentNudgeProps) => {
  const { orderForm } = useCheckout()
  const { pageData } = useGlobalContext()
  const [isInView, setIsInView] = useState(false)
  const lastSentValuesRef = useRef<{
    missingAmount: number
    installmentTier: number
  } | null>(null)

  const fallbackTotalCartValue = parseCurrencyLabelToNumber(totalCartLabel)
  const cashDiscountBenefitIds = getCashDiscountBenefitIds(
    orderForm?.ratesAndBenefitsData?.rateAndBenefitsIdentifiers
  )

  const cashDiscountValue = getCashDiscountValue({
    items: orderForm?.items,
    cashDiscountBenefitIds,
  })

  const itemsTotalWithoutDiscount = parseFloat(
    formatTotalizersTotal(orderForm?.totalizers?.items)
  )

  const totalDiscountValue = getTotalDiscountValue(
    orderForm?.totalizers?.discounts
  )

  const discountWithoutCashDiscountValue = Math.max(
    totalDiscountValue - cashDiscountValue,
    0
  )

  const installmentBaseValue = Math.max(
    roundToTwoDecimalPlaces(
      itemsTotalWithoutDiscount - discountWithoutCashDiscountValue
    ),
    0
  )

  const hasInstallmentCalculationData = Boolean(orderForm?.items?.length)

  const totalCartValue = hasInstallmentCalculationData
    ? installmentBaseValue
    : fallbackTotalCartValue

  const isReachedMaxInstallment = totalCartValue >= MAX_INSTALLMENT_TIER.minCart
  const isValidTotalCartValue = Number.isFinite(totalCartValue)

  const nextTier =
    isValidTotalCartValue && !isReachedMaxInstallment
      ? INSTALLMENT_TIERS.find((tier) => tier.minCart > totalCartValue)
      : undefined

  const shouldRenderNudge =
    isValidTotalCartValue && (isReachedMaxInstallment || Boolean(nextTier))

  const missingValue = nextTier
    ? roundToTwoDecimalPlaces(nextTier.minCart - totalCartValue)
    : 0

  const installmentTier = isReachedMaxInstallment
    ? MAX_INSTALLMENT_TIER.installments
    : nextTier?.installments ?? 0

  useEffect(() => {
    if (!isInView) {
      lastSentValuesRef.current = null

      return
    }

    if (!shouldRenderNudge) {
      return
    }

    const hasSameValuesAsLastSent =
      lastSentValuesRef.current?.missingAmount === missingValue &&
      lastSentValuesRef.current?.installmentTier === installmentTier

    if (hasSameValuesAsLastSent) {
      return
    }

    lastSentValuesRef.current = {
      missingAmount: missingValue,
      installmentTier,
    }

    sendNudgetInstallmentBarEvent({
      pageData,
      missingAmount: missingValue,
      installmentTier,
    })
  }, [isInView, shouldRenderNudge, missingValue, installmentTier, pageData])

  if (!shouldRenderNudge) {
    return null
  }

  const progressPercent = getProgressPercent(totalCartValue)
  const progressWidthClass = getProgressWidthClass(progressPercent)
  const highlightedStep = isReachedMaxInstallment
    ? '10x'
    : getHighlightedStep({
        totalCartValue,
        nextInstallment: nextTier?.installments,
      })

  const messageContent = isReachedMaxInstallment ? (
    <>
      <span className="mr-2" aria-hidden>
        ✅
      </span>
      {'Você pode parcelar em até '}
      <span className="text-primaryBlue font-semibold">10x</span>
      {' sem juros!'}
    </>
  ) : (
    <>
      <span className="mr-2" aria-hidden>
        💳
      </span>
      {'Faltam '}
      <span className="text-primaryBlue font-semibold">
        {formatCurrency(missingValue)}
      </span>
      {' para parcelar em até '}
      <span className="text-primaryBlue font-semibold">
        {`${nextTier?.installments}x`}
      </span>
      {' sem juros!'}
    </>
  )

  return (
    <InView
      as="div"
      className="rounded-2xl bg-restructure-background-neutral-4 px-4 py-3"
      data-testid="cart-feedback-installment-nudge"
      onChange={(inView) => setIsInView(inView)}
    >
      <p
        className="text-sm leading-[24px] text-restructure-primary font-medium"
        data-testid="cart-feedback-installment-nudge-message"
      >
        {messageContent}
      </p>

      <div className="mt-3">
        <div className="h-2 rounded-full bg-restructure-background-neutral-6">
          <div
            className={`h-full rounded-full bg-primaryBlue transition-all duration-300 ${progressWidthClass}`}
            data-testid="cart-feedback-installment-nudge-progress"
          />
        </div>

        <div className="mt-2 flex items-center justify-between text-xs leading-[20px] font-semibold text-restructure-neutral-16">
          {VISUAL_STEPS.map((step) => (
            <span
              key={step.label}
              className={
                step.label === highlightedStep
                  ? 'text-primaryBlue'
                  : 'text-restructure-neutral-16'
              }
            >
              {step.label}
            </span>
          ))}
        </div>
      </div>
    </InView>
  )
}

export default CartFeedbackInstallmentNudge
